import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import LoginRounded from '@mui/icons-material/LoginRounded';
import TelegramIcon from '@mui/icons-material/Telegram';
import YouTubeIcon from '@mui/icons-material/YouTube';

import { trackGtmEvent } from '../../app/analytics';
import axios from '../../axios';
import { applySeo } from '../../seo';
import { getStrategyLabel } from '../bots/strategyLabels';
import InfoHint from '../common/InfoHint';
import {
  CombinedPairRatingItem,
  PairRecommendationResponse,
  RatingPeriod,
  formatNumber,
  formatPercent,
  formatScore,
  isFiniteNumber,
} from '../ratings/pairRecommendations';
import { MaintenanceStatus } from './maintenance';

interface Props {
  maintenance?: MaintenanceStatus;
}

type LanguageCode = 'en' | 'ru' | 'ua';

type HighlightCardConfig = {
  key: string;
  title: string;
  accentColor: string;
  item: CombinedPairRatingItem | null;
  metrics: Array<{
    label: string;
    value: string;
    hintTitle?: string;
    hintBody?: string;
  }>;
};

const LANDING_INDEXABLE_HOSTS = new Set(['bitbothub.com', 'www.bitbothub.com']);
const LANDING_SOCIAL_LINKS = [
  'https://www.youtube.com/@Bitbothub-b1v',
  'https://t.me/bitbothub',
];
const LANDING_CONTACT_EMAIL = 'bitbothub@gmail.com';
const LOCALE_TO_HTML_LANG: Record<LanguageCode, string> = {
  en: 'en',
  ru: 'ru',
  ua: 'uk',
};
const LOCALE_TO_OG_LOCALE: Record<LanguageCode, string> = {
  en: 'en_US',
  ru: 'ru_RU',
  ua: 'uk_UA',
};

const LandingPage: React.FC<Props> = ({ maintenance }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const maintenanceEnabled = Boolean(maintenance?.enabled);
  const [ratingPeriod, setRatingPeriod] = useState<RatingPeriod>('week');
  const [ratingData, setRatingData] = useState<PairRecommendationResponse | null>(null);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [ratingError, setRatingError] = useState(false);
  const [shouldLoadRatings, setShouldLoadRatings] = useState(false);
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>('en');
  const ratingsSectionRef = useRef<HTMLDivElement | null>(null);
  const initialModalFlowCheckedRef = useRef(false);

  const features = useMemo(
    () =>
      [1, 2, 3, 4, 5, 6, 7, 8].map((idx) => ({
        title: t(`landing.feature${idx}Title`),
        text: t(`landing.feature${idx}Text`),
      })),
    [t]
  );

  const stableItems = useMemo(() => (ratingData?.leaderboards.stable || []).slice(0, 6), [ratingData]);
  const profitItems = useMemo(() => (ratingData?.leaderboards.profit || []).slice(0, 6), [ratingData]);

  const normalizeLanguage = (value?: string | null): LanguageCode => {
    if (value === 'ru' || value === 'ua' || value === 'en') return value;
    return 'en';
  };

  const seoLanguage = useMemo(
    () => normalizeLanguage(i18n.resolvedLanguage || i18n.language),
    [i18n.language, i18n.resolvedLanguage]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hostname = window.location.hostname.toLowerCase();
    const isIndexableHost = LANDING_INDEXABLE_HOSTS.has(hostname);
    const origin = isIndexableHost ? 'https://bitbothub.com' : window.location.origin;
    const canonicalUrl = `${origin}/`;
    const imageUrl = `${origin}/logo512.png`;
    const htmlLang = LOCALE_TO_HTML_LANG[seoLanguage];
    const ogLocale = LOCALE_TO_OG_LOCALE[seoLanguage];
    const title = `BitBotHub | ${t('landing.seoTitle')}`;
    const description = t('landing.seoDescription');

    applySeo({
      title,
      description,
      robots: isIndexableHost
        ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        : 'noindex, nofollow',
      canonical: canonicalUrl,
      lang: htmlLang,
      author: 'BitBotHub',
      applicationName: 'BitBotHub',
      openGraph: {
        type: 'website',
        url: canonicalUrl,
        title,
        description,
        image: imageUrl,
        imageAlt: t('landing.heroTitle'),
        siteName: 'BitBotHub',
        locale: ogLocale,
      },
      twitter: {
        card: 'summary_large_image',
        title,
        description,
        image: imageUrl,
        imageAlt: t('landing.heroTitle'),
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': `${canonicalUrl}#organization`,
            name: 'BitBotHub',
            url: canonicalUrl,
            logo: imageUrl,
            email: LANDING_CONTACT_EMAIL,
            sameAs: LANDING_SOCIAL_LINKS,
            contactPoint: [
              {
                '@type': 'ContactPoint',
                contactType: 'customer support',
                email: LANDING_CONTACT_EMAIL,
                availableLanguage: ['en', 'ru', 'uk'],
              },
            ],
          },
          {
            '@type': 'WebSite',
            '@id': `${canonicalUrl}#website`,
            url: canonicalUrl,
            name: 'BitBotHub',
            description,
            inLanguage: htmlLang,
            publisher: {
              '@id': `${canonicalUrl}#organization`,
            },
          },
          {
            '@type': 'SoftwareApplication',
            '@id': `${canonicalUrl}#application`,
            name: 'BitBotHub',
            url: canonicalUrl,
            applicationCategory: 'FinanceApplication',
            operatingSystem: 'Web',
            description,
            publisher: {
              '@id': `${canonicalUrl}#organization`,
            },
            featureList: features.map((feature) => feature.title),
          },
        ],
      },
    });
  }, [features, seoLanguage, t, i18n.language]);

  useEffect(() => {
    if (shouldLoadRatings || typeof window === 'undefined') return;
    const timerId = window.setTimeout(() => setShouldLoadRatings(true), 2500);
    return () => window.clearTimeout(timerId);
  }, [shouldLoadRatings]);

  useEffect(() => {
    if (shouldLoadRatings || typeof window === 'undefined') return;
    const section = ratingsSectionRef.current;
    if (!section) return;

    if (!('IntersectionObserver' in window)) {
      setShouldLoadRatings(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoadRatings(true);
          observer.disconnect();
        }
      },
      { rootMargin: '280px 0px' }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [shouldLoadRatings]);

  useEffect(() => {
    if (!shouldLoadRatings) return;
    const controller = new AbortController();

    const loadRatings = async () => {
      setRatingLoading(true);
      setRatingError(false);

      try {
        const params = new URLSearchParams();
        params.set('period', ratingPeriod);
        params.set('limit', '6');

        const response = await axios.get(`/global-stats/recommendations-public?${params.toString()}`, {
          signal: controller.signal,
        });

        setRatingData(response.data as PairRecommendationResponse);
      } catch (error: any) {
        if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
          setRatingError(true);
        }
      } finally {
        setRatingLoading(false);
      }
    };

    loadRatings();
    return () => controller.abort();
  }, [ratingPeriod, shouldLoadRatings]);

  useEffect(() => {
    if (typeof window === 'undefined' || initialModalFlowCheckedRef.current) return;
    initialModalFlowCheckedRef.current = true;

    const storedLanguage = localStorage.getItem('language');
    const normalized = normalizeLanguage(storedLanguage || i18n.language);
    setSelectedLanguage(normalized);

    if (i18n.language !== normalized) {
      void i18n.changeLanguage(normalized);
    }

    if (!storedLanguage) {
      setLanguageModalOpen(true);
      return;
    }

  }, [i18n, i18n.language]);

  const highlightCards = useMemo<HighlightCardConfig[]>(
    () => [
      {
        key: 'mostStablePair',
        title: t('bots.ratingsMostStablePair'),
        accentColor: '#0284c7',
        item: ratingData?.highlights.mostStablePair ?? null,
        metrics: [
          {
            label: t('bots.ratingsSharpeShort'),
            value: formatNumber(ratingData?.highlights.mostStablePair?.sharpeRatio),
            hintTitle: t('landing.recommendationsSharpeHintTitle'),
            hintBody: t('landing.recommendationsSharpeHintBody'),
          },
          {
            label: t('bots.ratingsMaxDrawdownShort'),
            value: formatPercent(ratingData?.highlights.mostStablePair?.maxDrawdown),
          },
          {
            label: t('bots.ratingsWinRateShort'),
            value: formatPercent(ratingData?.highlights.mostStablePair?.winRate),
          },
        ],
      },
      {
        key: 'bestModerateRiskPair',
        title: t('bots.ratingsBestModerateRiskPair'),
        accentColor: '#16a34a',
        item: ratingData?.highlights.bestModerateRiskPair ?? null,
        metrics: [
          {
            label: t('bots.ratingsProfitPercentShort'),
            value: formatPercent(ratingData?.highlights.bestModerateRiskPair?.profitPercent),
          },
          {
            label: t('bots.ratingsSharpeShort'),
            value: formatNumber(ratingData?.highlights.bestModerateRiskPair?.sharpeRatio),
            hintTitle: t('landing.recommendationsSharpeHintTitle'),
            hintBody: t('landing.recommendationsSharpeHintBody'),
          },
          {
            label: t('bots.ratingsStabilityShort'),
            value: formatScore(ratingData?.highlights.bestModerateRiskPair?.stabilityScore),
          },
        ],
      },
      {
        key: 'mostReliableDrawdownPair',
        title: t('bots.ratingsMostReliableDrawdownPair'),
        accentColor: '#ea580c',
        item: ratingData?.highlights.mostReliableDrawdownPair ?? null,
        metrics: [
          {
            label: t('bots.ratingsMaxDrawdownShort'),
            value: formatPercent(ratingData?.highlights.mostReliableDrawdownPair?.maxDrawdown),
          },
          {
            label: t('bots.ratingsAverageReturnShort'),
            value: formatPercent(ratingData?.highlights.mostReliableDrawdownPair?.averageReturn),
          },
          {
            label: t('bots.ratingsSharpeShort'),
            value: formatNumber(ratingData?.highlights.mostReliableDrawdownPair?.sharpeRatio),
            hintTitle: t('landing.recommendationsSharpeHintTitle'),
            hintBody: t('landing.recommendationsSharpeHintBody'),
          },
        ],
      },
      {
        key: 'topProfitPair',
        title: t('bots.ratingsTopProfitPair'),
        accentColor: '#7c3aed',
        item: ratingData?.highlights.topProfitPair ?? null,
        metrics: [
          {
            label: t('bots.ratingsProfitPercentShort'),
            value: formatPercent(ratingData?.highlights.topProfitPair?.profitPercent),
          },
          {
            label: t('bots.ratingsTradesShort'),
            value: String(ratingData?.highlights.topProfitPair?.tradesCount ?? '-'),
          },
          {
            label: t('bots.ratingsStableRank'),
            value: isFiniteNumber(ratingData?.highlights.topProfitPair?.stableRank)
              ? `#${ratingData?.highlights.topProfitPair?.stableRank}`
              : '-',
            hintTitle: t('landing.recommendationsStableRankHintTitle'),
            hintBody: t('landing.recommendationsStableRankHintBody'),
          },
        ],
      },
    ],
    [ratingData, t]
  );

  const formatPairLabel = (item: CombinedPairRatingItem | null | undefined) => {
    const symbol = String(item?.symbol ?? '').trim().toUpperCase();
    const strategyType = String(item?.strategyType ?? '').trim();
    const strategyLabel = strategyType ? getStrategyLabel(undefined, strategyType) : '';
    return strategyLabel ? `${symbol} (${strategyLabel})` : symbol || '-';
  };

  const handleLanguageSelect = (language: LanguageCode) => {
    setSelectedLanguage(language);
  };

  const handleLanguageConfirm = async () => {
    localStorage.setItem('language', selectedLanguage);
    await i18n.changeLanguage(selectedLanguage);
    setLanguageModalOpen(false);
  };


  const renderLeaderboardTable = ({
    title,
    subtitle,
    items,
    mode,
  }: {
    title: string;
    subtitle: string;
    items: CombinedPairRatingItem[];
    mode: 'stable' | 'profit';
  }) => {
    const metricLabel =
      mode === 'stable' ? t('bots.ratingsAverageReturn') : t('bots.ratingsProfitPercent');

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.25 },
          borderRadius: 3,
          border: `1px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#e6ecf5'}`,
          background: theme.palette.mode === 'dark' ? 'rgba(10, 15, 26, 0.58)' : 'rgba(255, 255, 255, 0.78)',
        }}
      >
        <Stack spacing={1.25}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>

          <TableContainer
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#e6ecf5'}`,
              overflowX: 'auto',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 52 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('bots.ratingsSymbol')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {metricLabel}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {t('bots.ratingsNoData')}
                    </TableCell>
                  </TableRow>
                )}

                {items.map((item, index) => (
                  <TableRow key={`${mode}-${item.symbol}-${item.strategyType || 'all'}-${index}`} hover>
                    <TableCell>{mode === 'stable' ? item.stableRank ?? index + 1 : item.profitRank ?? index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                        <span>{String(item.symbol || '').trim().toUpperCase()}</span>
                        {item.strategyType && (
                          <Box
                            component="span"
                            sx={{
                              fontSize: '0.7em',
                              fontWeight: 600,
                              color: 'text.secondary',
                              lineHeight: 1,
                            }}
                          >
                            {getStrategyLabel(undefined, item.strategyType)}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color:
                          mode === 'profit' && isFiniteNumber(item.profitPercent)
                            ? item.profitPercent >= 0
                              ? '#2e7d32'
                              : '#d32f2f'
                            : 'inherit',
                        fontWeight: 700,
                      }}
                    >
                      {mode === 'stable'
                        ? formatPercent(item.averageReturn)
                        : formatPercent(item.profitPercent)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
    );
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        width: '100%',
        background:
          theme.palette.mode === 'dark'
            ? 'radial-gradient(1200px 600px at 20% 10%, rgba(76, 122, 229, 0.20), transparent 60%), radial-gradient(900px 500px at 80% 30%, rgba(29, 185, 84, 0.12), transparent 55%)'
            : 'radial-gradient(1200px 600px at 20% 10%, rgba(25, 118, 210, 0.14), transparent 60%), radial-gradient(900px 500px at 80% 30%, rgba(29, 185, 84, 0.10), transparent 55%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        pb: { xs: 4, sm: 6 },
      }}
    >
      <Container maxWidth="lg" sx={{ pt: { xs: 1, sm: 2 } }}>
        <Stack spacing={{ xs: 3, sm: 4 }}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.1, sm: 1.8 },
                borderRadius: 2.5,
                border: `1px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#e6ecf5'}`,
                background: theme.palette.mode === 'dark' ? 'rgba(10, 15, 26, 0.82)' : 'rgba(255, 255, 255, 0.82)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={{ xs: 0.75, sm: 1.25 }}
                sx={{ minWidth: 0 }}
              >
                <Stack
                  direction="row"
                  spacing={{ xs: 0.6, sm: 1 }}
                  alignItems="center"
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 900,
                      letterSpacing: 0.1,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      flexShrink: 0,
                    }}
                  >
                    BitBotHub
                  </Typography>
                  <Chip
                    size="small"
                    label={t('landing.tagline')}
                    sx={{
                      fontWeight: 700,
                      minWidth: 0,
                      maxWidth: { xs: '38vw', sm: '100%' },
                      fontSize: { xs: '0.62rem', sm: '0.75rem' },
                      '& .MuiChip-label': {
                        px: { xs: 0.8, sm: 1.5 },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                    color="primary"
                    variant={theme.palette.mode === 'dark' ? 'outlined' : 'filled'}
                  />
                </Stack>

                <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} sx={{ flexShrink: 0 }}>
                  <Button
                    variant="outlined"
                    startIcon={<LoginRounded />}
                    disabled={maintenanceEnabled}
                    onClick={() => navigate('/login')}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 800,
                      minWidth: 0,
                      fontSize: { xs: '0.74rem', sm: '0.875rem' },
                      px: { xs: 1, sm: 1.75 },
                      py: { xs: 0.6, sm: 0.75 },
                      whiteSpace: 'nowrap',
                      '& .MuiButton-startIcon': {
                        mr: { xs: 0.5, sm: 1 },
                      },
                      '& .MuiButton-startIcon > *:first-of-type': {
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                      },
                    }}
                  >
                    {t('landing.login')}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Box>

          {maintenanceEnabled && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                {t('maintenance.title')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {maintenance?.message || t('maintenance.message')}
              </Typography>
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              border: `1px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#e6ecf5'}`,
              background: theme.palette.mode === 'dark' ? 'rgba(10, 15, 26, 0.6)' : 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Stack spacing={2.5}>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 900,
                  letterSpacing: -0.6,
                  lineHeight: 1.05,
                  fontSize: { xs: '2.05rem', sm: '2.6rem', md: '3.1rem' },
                }}
              >
                {t('landing.heroTitle')}
              </Typography>

              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  maxWidth: 900,
                  lineHeight: 1.4,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                }}
              >
                {t('landing.heroSubtitle')}
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.25}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    disabled={maintenanceEnabled}
                    onClick={() => {
                      trackGtmEvent('click_home_view_signals');
                      navigate('/signals');
                    }}
                    sx={{
                      borderRadius: 2,
                      minHeight: 50,
                      boxSizing: 'border-box',
                      border: '2px solid transparent',
                      textTransform: 'none',
                      fontWeight: 950,
                      px: 3,
                      py: 1.05,
                      boxShadow: '0 12px 26px rgba(25, 118, 210, 0.28)',
                      background:
                        theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #416de8, #38bdf8) padding-box, conic-gradient(from 90deg, #22d3ee, #22c55e, #fde047, #ec4899, #8b5cf6, #22d3ee) border-box'
                          : 'linear-gradient(135deg, #3f73e6, #49c7f3) padding-box, conic-gradient(from 90deg, #06b6d4, #22c55e, #facc15, #ec4899, #7c3aed, #06b6d4) border-box',
                      backgroundSize: '100% 100%, 220% 220%',
                      animation: 'landingSignalBorder 4s linear infinite',
                      '@keyframes landingSignalBorder': {
                        '0%': { backgroundPosition: '0 0, 0% 50%' },
                        '50%': { backgroundPosition: '0 0, 100% 50%' },
                        '100%': { backgroundPosition: '0 0, 0% 50%' },
                      },
                      '&:hover': {
                        boxShadow: '0 14px 30px rgba(25, 118, 210, 0.28)',
                      },
                    }}
                  >
                    {t('landing.viewSignals')}
                  </Button>

                  <Button
                    variant="contained"
                    size="large"
                    disabled={maintenanceEnabled}
                    onClick={() => navigate('/register')}
                    sx={{
                      borderRadius: 2,
                      minHeight: 50,
                      boxSizing: 'border-box',
                      textTransform: 'none',
                      fontWeight: 900,
                      px: 3,
                      py: 1.05,
                      boxShadow: 'none',
                      '&:hover': { boxShadow: 'none' },
                    }}
                  >
                    {t('landing.cta')}
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    component="a"
                    href="https://news.bitbothub.com"
                    target="_blank"
                    rel="noreferrer noopener"
                    sx={{
                      borderRadius: 2,
                      minHeight: 50,
                      boxSizing: 'border-box',
                      textTransform: 'none',
                      fontWeight: 800,
                      px: 3,
                      py: 1.05,
                    }}
                  >
                    BitBotHub NEWS
                  </Button>
                </Stack>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    const el = document.getElementById('landing-features');
                    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    borderRadius: 2,
                    minHeight: 50,
                    boxSizing: 'border-box',
                    textTransform: 'none',
                    fontWeight: 800,
                    px: 3,
                    py: 1.05,
                  }}
                >
                  {t('landing.learnMore')}
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                {t('landing.riskDisclaimer')}
              </Typography>
            </Stack>
          </Paper>

          <Box component="section" id="landing-features" aria-labelledby="landing-features-title">
            <Stack spacing={1.25} sx={{ mb: 2 }}>
              <Typography
                id="landing-features-title"
                variant="h4"
                component="h2"
                sx={{
                  fontWeight: 900,
                  letterSpacing: -0.3,
                  fontSize: { xs: '1.65rem', sm: '2rem' },
                }}
              >
                {t('landing.featuresTitle')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 880 }}>
                {t('landing.featuresSubtitle')}
              </Typography>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              {features.map((feature) => (
                <Paper
                  key={feature.title}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#e6ecf5'}`,
                    background: theme.palette.mode === 'dark' ? 'rgba(26, 32, 44, 0.55)' : 'rgba(255, 255, 255, 0.75)',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 0.5 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.text}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>

          <Paper
            component="section"
            aria-labelledby="landing-recommendations-title"
            ref={ratingsSectionRef}
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              border: `1px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#e6ecf5'}`,
              background: theme.palette.mode === 'dark' ? 'rgba(26, 32, 44, 0.55)' : 'rgba(255, 255, 255, 0.78)',
            }}
          >
            <Stack spacing={1.75}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                gap={1.25}
              >
                <Box>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography
                      id="landing-recommendations-title"
                      variant="h6"
                      component="h2"
                      sx={{ fontWeight: 900 }}
                    >
                      {t('landing.recommendationsTitle')}
                    </Typography>
                    <InfoHint
                      ariaLabel={t('landing.recommendationsHintTitle')}
                      title={t('landing.recommendationsHintTitle')}
                      content={t('landing.recommendationsHintBody')}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {t('landing.recommendationsSubtitle')}
                  </Typography>
                </Box>

                <ToggleButtonGroup
                  value={ratingPeriod}
                  exclusive
                  size="small"
                  disabled={!shouldLoadRatings}
                  onChange={(_, value: RatingPeriod | null) => {
                    if (value) setRatingPeriod(value);
                  }}
                  sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                >
                  <ToggleButton value="day">{t('bots.ratingsPeriodDay')}</ToggleButton>
                  <ToggleButton value="week">{t('bots.ratingsPeriodWeek')}</ToggleButton>
                  <ToggleButton value="month">{t('bots.ratingsPeriodMonth')}</ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              {shouldLoadRatings && ratingData && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={t('landing.recommendationsSummary', {
                      stable: ratingData.summary.stablePairsCount,
                      profit: ratingData.summary.profitPairsCount,
                      combined: ratingData.summary.combinedPairsCount,
                    })}
                  />
                  {ratingData.lastUpdated && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${t('bots.ratingsUpdated')}: ${new Date(ratingData.lastUpdated).toUTCString()}`}
                    />
                  )}
                </Stack>
              )}

              {(!shouldLoadRatings || ratingLoading) && (
                <Typography variant="body2" color="text.secondary">
                  {t('landing.recommendationsLoading')}
                </Typography>
              )}

              {shouldLoadRatings && !ratingLoading && ratingError && (
                <Typography variant="body2" color="error">
                  {t('bots.ratingsLoadError')}
                </Typography>
              )}

              {shouldLoadRatings && !ratingLoading && !ratingError && (
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        xl: 'repeat(4, minmax(0, 1fr))',
                      },
                      gap: 1.5,
                    }}
                  >
                    {highlightCards.map((card) => (
                      <Paper
                        key={card.key}
                        elevation={0}
                        sx={{
                          p: 1.75,
                          borderRadius: 2.5,
                          border: `1px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#dbe5f2'}`,
                          background:
                            theme.palette.mode === 'dark'
                              ? 'linear-gradient(180deg, rgba(13, 18, 33, 0.92), rgba(13, 18, 33, 0.72))'
                              : 'linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(246, 250, 255, 0.88))',
                          boxShadow:
                            theme.palette.mode === 'dark'
                              ? '0 10px 30px rgba(0, 0, 0, 0.18)'
                              : '0 10px 30px rgba(15, 23, 42, 0.06)',
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Typography
                            variant="overline"
                            sx={{ fontWeight: 900, lineHeight: 1.2, color: card.accentColor }}
                          >
                            {card.title}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                            {formatPairLabel(card.item)}
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                              gap: 1,
                            }}
                          >
                            {card.metrics.map((metric) => (
                              <Box key={`${card.key}-${metric.label}`}>
                                <Stack direction="row" spacing={0.4} alignItems="center">
                                  <Typography variant="caption" color="text.secondary">
                                    {metric.label}
                                  </Typography>
                                  {metric.hintBody ? (
                                    <InfoHint
                                      ariaLabel={metric.hintTitle || metric.label}
                                      title={metric.hintTitle}
                                      content={metric.hintBody}
                                      buttonSize={18}
                                      iconSize={11}
                                      maxWidth={280}
                                    />
                                  ) : null}
                                </Stack>
                                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                  {metric.value}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        lg: 'repeat(2, minmax(0, 1fr))',
                      },
                      gap: 2,
                    }}
                  >
                    {renderLeaderboardTable({
                      title: t('bots.ratingsStableLeaderboard'),
                      subtitle: t('landing.recommendationsStableLeaderboardHint'),
                      items: stableItems,
                      mode: 'stable',
                    })}
                    {renderLeaderboardTable({
                      title: t('bots.ratingsProfitLeaderboard'),
                      subtitle: t('landing.recommendationsProfitLeaderboardHint'),
                      items: profitItems,
                      mode: 'profit',
                    })}
                  </Box>
                </Stack>
              )}
            </Stack>
          </Paper>

          <Stack
            component="footer"
            direction="row"
            spacing={1.5}
            justifyContent="center"
            alignItems="center"
          >
            <IconButton
              component="a"
              href="https://www.youtube.com/@Bitbothub-b1v"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="YouTube BitBotHub"
              sx={{ color: '#ff0000' }}
            >
              <YouTubeIcon />
            </IconButton>
            <IconButton
              component="a"
              href="https://t.me/bitbothub"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Telegram BitBotHub"
              sx={{ color: '#229ED9' }}
            >
              <TelegramIcon />
            </IconButton>
            <IconButton
              component="a"
              href="mailto:bitbothub@gmail.com"
              aria-label="BitBotHub email"
              sx={{ color: theme.palette.mode === 'dark' ? '#9ec5fe' : '#1976d2' }}
            >
              <EmailIcon />
            </IconButton>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', opacity: 0.8, mt: 1 }}>
            {t('landing.footer')}
          </Typography>
        </Stack>
      </Container>

      <Dialog
        open={languageModalOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          setLanguageModalOpen(false);
        }}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('landing.languageModalTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('landing.languageModalDescription')}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 1,
            }}
          >
            <Button
              variant={selectedLanguage === 'en' ? 'contained' : 'outlined'}
              onClick={() => handleLanguageSelect('en')}
              fullWidth
              sx={{ minWidth: 0, px: { xs: 1, sm: 2 }, py: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              EN
            </Button>
            <Button
              variant={selectedLanguage === 'ua' ? 'contained' : 'outlined'}
              onClick={() => handleLanguageSelect('ua')}
              fullWidth
              sx={{ minWidth: 0, px: { xs: 1, sm: 2 }, py: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              UA
            </Button>
            <Button
              variant={selectedLanguage === 'ru' ? 'contained' : 'outlined'}
              onClick={() => handleLanguageSelect('ru')}
              fullWidth
              sx={{ minWidth: 0, px: { xs: 1, sm: 2 }, py: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              RU
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLanguageConfirm} variant="contained">
            {t('landing.languageContinue')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LandingPage;



